import React from 'react';
import { Loading } from '../../../../../components';

type LatencyCardProps = {
  title: string;
  subtitle?: string;
  loading?: boolean;
  isEmpty?: boolean;
  height?: string;
  children: React.ReactNode;
};

export const LatencyCard: React.FC<LatencyCardProps> = ({
  title,
  subtitle,
  loading,
  isEmpty,
  height = 'h-[340px]',
  children,
}) => (
  <div
    className={`bg-white rounded-card shadow-sm p-6 flex flex-col ${height}`}
  >
    <div className="mb-4">
      <div className="text-base font-semibold text-primary">{title}</div>
      {subtitle && <div className="text-small text-secondary">{subtitle}</div>}
    </div>
    <div className="flex-1 min-h-0">
      {loading ? (
        <div className="flex items-center justify-center h-full">
          <Loading />
        </div>
      ) : isEmpty ? (
        <div className="flex items-center justify-center h-full text-secondary">
          No data available
        </div>
      ) : (
        children
      )}
    </div>
  </div>
);

// Shared recharts Tooltip content style — a clean small card instead of the
// default (which renders as an oversized white box on hover).
export const TOOLTIP_STYLE: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  fontSize: 12,
  padding: '6px 10px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};

export const TOOLTIP_LABEL_STYLE: React.CSSProperties = {
  color: '#6b7280',
  marginBottom: 2,
};

type LatencyTooltipEntry = {
  name?: React.ReactNode;
  value?: number | string;
  color?: string;
};

// Custom tooltip content. Returning null when inactive prevents recharts from
// leaving an empty white box behind after the pointer leaves the chart.
export const LatencyTooltip: React.FC<{
  active?: boolean;
  label?: React.ReactNode;
  payload?: LatencyTooltipEntry[];
  unit?: string;
}> = ({ active, label, payload, unit }) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }
  return (
    <div style={TOOLTIP_STYLE}>
      {label !== undefined && label !== null && label !== '' && (
        <div style={TOOLTIP_LABEL_STYLE}>{label}</div>
      )}
      {payload.map((entry, index) => (
        <div key={index} style={{ color: entry.color ?? '#111827' }}>
          {entry.name}: {Math.round(Number(entry.value))}
          {unit ? ` ${unit}` : ''}
        </div>
      ))}
    </div>
  );
};

export const fmtMs = (v: number | null | undefined): string =>
  v === null || v === undefined ? '—' : `${Math.round(v)} ms`;

export const fmtNum = (v: number | null | undefined): string =>
  v === null || v === undefined ? '—' : Math.round(v).toLocaleString();
